"use client";

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  List,
  ChartBar,
  Info,
  MagnifyingGlass,
  Funnel,
  CaretDown,
  Check,
  X,
} from "@phosphor-icons/react/dist/ssr";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CustomTooltip from "@/components/CustomTooltip";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight } from "@phosphor-icons/react/dist/ssr";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useSector } from "@/hooks/useSector";
import "flag-icons/css/flag-icons.min.css";

// Import sidebar tiles and charts
import HighestUnicorn from "./tiles/HighestUnicorn";
import UnicornDonut from "./charts/UnicornDonut";
import TotalFunding from "./tiles/TotalFunding";
import MedianTimeUnicorn from "./tiles/MedianTimeUnicorn";
import FastestUnicorn from "./tiles/FastestUnicorn";
import ShareOfRegion from "./charts/ShareOfRegion";
import UnicornCountValuation from "./charts/UnicornCountValuation";
import UnicornBySector from "./charts/UnicornBySector";
import GDPPerCapita from "./charts/GDPPerCapita";
import TotalPopulation from "./charts/TotalPopulation";
import InternetPenetration from "./charts/InternetPenetration";
import BankingPenetration from "./charts/BankingPenetration";
import CarouselReport from "../parts/CarouselReport";
import CarouselSpotlight from "../parts/CarouselSpotlight";

const ORIGIN_FLAG_MAP = {
  India: "fi-in",
  Israel: "fi-il",
  "United Arab Emirates": "fi-ae",
  UAE: "fi-ae",
  "Saudi Arabia": "fi-sa",
  Turkey: "fi-tr",
  Egypt: "fi-eg",
  "South Africa": "fi-za",
  Nigeria: "fi-ng",
  Kenya: "fi-ke",
  Pakistan: "fi-pk",
  Iran: "fi-ir",
  Qatar: "fi-qa",
  Bahrain: "fi-bh",
  Kuwait: "fi-kw",
  Jordan: "fi-jo",
  Morocco: "fi-ma",
  Tunisia: "fi-tn",
  Seychelles: "fi-sc",
  Senegal: "fi-sn",
  Russia: "fi-ru",
  China: "fi-cn",
  "People's Republic of China": "fi-cn",
  "United States": "fi-us",
  "United Kingdom": "fi-gb",
  Singapore: "fi-sg",
  Indonesia: "fi-id",
  Malaysia: "fi-my",
  Japan: "fi-jp",
  "South Korea": "fi-kr",
  Germany: "fi-de",
  France: "fi-fr",
  Brazil: "fi-br",
  Canada: "fi-ca",
  Australia: "fi-au",
};

function getOriginFlag(countryName) {
  return ORIGIN_FLAG_MAP[countryName] || "fi-xx";
}

const ORIGIN_TYPE_STYLES = {
  homegrown: "bg-teal-400/20 text-teal-400 border-teal-400/30",
  transplant_in: "bg-purple-400/20 text-purple-400 border-purple-400/30",
  transplant_out: "bg-[#E06B5A]/20 text-[#E06B5A] border-[#E06B5A]/30",
  transplant: "bg-purple-400/20 text-purple-400 border-purple-400/30",
};

const ORIGIN_TYPE_LABELS = {
  homegrown: "Homegrown",
  transplant_in: "Transplant-In",
  transplant_out: "Transplant-Out",
  transplant: "Transplant",
};

export default function MobileResizableDrawer({
  // Sidebar props
  regionData,
  countryData,
  selectedCountryName,
  selectedRegionName,
  unicornStats,
  dataLoading,
  specialReports,
  spotlights,
  podcasts,
  otherContent,
  exploreTooltip,
  // Unicorn list props
  unicorns,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  formatCurrency,
  formatDate,
  formatTimeToUnicornYears,
  getUnicornStatus,
  getLatestStage,
  statusFilter,
  setStatusFilter,
  statusCounts,
  onSearchChange,
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { data: sectorList } = useSector({ dropdown: true });

  const [activeTab, setActiveTab] = useState("data");
  const [drawerHeight, setDrawerHeight] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(60);
  const [localSearch, setLocalSearch] = useState("");
  const [sectorDropdownOpen, setSectorDropdownOpen] = useState(false);
  const [sectorSearch, setSectorSearch] = useState("");

  const drawerRef = useRef(null);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const listScrollRef = useRef(null);
  const sectorDropdownRef = useRef(null);

  // Debounce search input → notify parent
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange?.(localSearch.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  // Close sector dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        sectorDropdownRef.current &&
        !sectorDropdownRef.current.contains(e.target)
      ) {
        setSectorDropdownOpen(false);
        setSectorSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll list to top when status filter changes
  useEffect(() => {
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0;
    }
  }, [statusFilter]);

  // Sector URL state
  const sectorsParam = searchParams.get("sectors");
  const selectedSectorIds = useMemo(
    () => (sectorsParam ? sectorsParam.split(",") : []),
    [sectorsParam],
  );

  const filteredSectorList = useMemo(() => {
    return (sectorList || [])
      .filter((s) => s.name)
      .filter((s) => s.name.toLowerCase().includes(sectorSearch.toLowerCase()))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [sectorList, sectorSearch]);

  const toggleSector = (sectorId) => {
    const id = sectorId.toString();
    const params = new URLSearchParams(searchParams.toString());
    const next = selectedSectorIds.includes(id)
      ? selectedSectorIds.filter((s) => s !== id)
      : [...selectedSectorIds, id];
    if (next.length === 0) {
      params.delete("sectors");
    } else {
      params.set("sectors", next.join(","));
    }
    router.push(`${pathname}?${params.toString()}`);
  };

  const clearSectors = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("sectors");
    router.push(`${pathname}?${params.toString()}`);
  };

  const getSectorLabel = () => {
    if (selectedSectorIds.length === 0) return "Sector";
    if (selectedSectorIds.length === 1) {
      const s = (sectorList || []).find(
        (sec) => sec.id.toString() === selectedSectorIds[0],
      );
      return s ? s.name : "1 Sector";
    }
    return `${selectedSectorIds.length} Sectors`;
  };

  const toggleStatus = (key) => {
    if (!statusFilter || !setStatusFilter) return;
    const count = statusCounts?.[key] || 0;
    if (count === 0) return;
    setStatusFilter({ ...statusFilter, [key]: !statusFilter[key] });
  };

  const lastUnicornElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingRef.current) {
            onLoadMore();
          }
        },
        { threshold: 0.5 },
      );
      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMore, hasMore, onLoadMore],
  );

  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    setStartY(clientY);
    setStartHeight(drawerHeight);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
    const windowHeight = window.innerHeight;
    const deltaY = startY - clientY;
    const deltaPercent = (deltaY / windowHeight) * 100;
    let newHeight = startHeight + deltaPercent;
    newHeight = Math.max(30, Math.min(85, newHeight));
    setDrawerHeight(newHeight);
  };

  const handleDragEnd = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      document.addEventListener("touchmove", handleDragMove);
      document.addEventListener("touchend", handleDragEnd);
      return () => {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
        document.removeEventListener("touchmove", handleDragMove);
        document.removeEventListener("touchend", handleDragEnd);
      };
    }
  }, [isDragging, startY, startHeight]);

  return (
    <div
      ref={drawerRef}
      className='fixed bottom-0 left-0 right-0 z-30 flex flex-col transition-none border-t shadow-2xl bg-background border-white/10 rounded-t-2xl'
      style={{ height: `${drawerHeight}vh` }}
    >
      {/* Drag Handle */}
      <div
        className='flex items-center justify-center flex-shrink-0 pt-3 pb-2 cursor-ns-resize active:cursor-grabbing touch-none'
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
      >
        <div className='w-12 h-1 rounded-full bg-white/30' />
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className='flex flex-col flex-1 min-h-0 overflow-hidden'
      >
        {/* Tab Switcher */}
        <div className='flex-shrink-0 px-4 pt-2 pb-3 border-b border-white/5'>
          <TabsList className='grid w-full grid-cols-2 p-1 rounded-lg bg-white/5'>
            <TabsTrigger
              value='data'
              className='flex items-center gap-2 data-[state=active]:bg-white/10 text-xs'
            >
              <ChartBar weight='bold' className='text-base' />
              <span>Statistics</span>
            </TabsTrigger>
            <TabsTrigger
              value='list'
              className='flex items-center gap-2 data-[state=active]:bg-white/10 text-xs'
            >
              <List weight='bold' className='text-base' />
              <span>Unicorn List</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Statistics Tab */}
        {activeTab === "data" && (
          <div className='flex-1 min-h-0 overflow-hidden'>
            <div className='h-full px-4 pb-6 overflow-y-auto'>
              <div className='pt-4 space-y-6'>
                <div className='grid items-start gap-3'>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/[0.07] border-orange-1/40'>
                    <HighestUnicorn
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <UnicornDonut
                      unicornStats={unicornStats}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                      isLoading={dataLoading}
                    />
                  </div>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <TotalFunding
                      unicornStats={unicornStats}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <MedianTimeUnicorn
                      unicornStats={unicornStats}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/[0.07] border-orange-1/40'>
                    <FastestUnicorn
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                      unicornStats={unicornStats}
                    />
                  </div>
                  <div className='px-4 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <ShareOfRegion
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 pt-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <UnicornCountValuation
                      unicornStats={unicornStats}
                      regionData={
                        regionData
                          ? { ...regionData, name: selectedRegionName }
                          : null
                      }
                      countryData={
                        countryData
                          ? { ...countryData, name: selectedCountryName }
                          : null
                      }
                    />
                  </div>
                  <div className='px-4 pt-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <UnicornBySector
                      unicornStats={unicornStats}
                      regionData={
                        regionData
                          ? { ...regionData, name: selectedRegionName }
                          : null
                      }
                      countryData={
                        countryData
                          ? { ...countryData, name: selectedCountryName }
                          : null
                      }
                    />
                  </div>
                  <div className='px-4 pt-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <GDPPerCapita
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 pt-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <TotalPopulation
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 pb-0 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <InternetPenetration
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                  <div className='px-4 pb-0 py-3.5 border rounded-xl bg-white/5 border-white/5'>
                    <BankingPenetration
                      regionData={regionData}
                      countryData={countryData}
                      selectedCountryName={selectedCountryName}
                      selectedRegionName={selectedRegionName}
                    />
                  </div>
                </div>

                {/* Explore Section */}
                <div className='w-full pb-4'>
                  <div className='flex items-center gap-2 mb-4'>
                    <p className='text-lg font-semibold'>Explore</p>
                    <CustomTooltip
                      trigger={
                        <span className='text-white/40 cursor-help'>
                          <Info className='size-4' />
                        </span>
                      }
                      content={
                        exploreTooltip ||
                        "Explore shows Lucidity content that best matches your current selection."
                      }
                    />
                  </div>
                  <div className='space-y-6'>
                    {specialReports.length > 0 && (
                      <div>
                        <div className='flex items-center justify-between mb-3'>
                          <p className='text-sm font-semibold'>
                            Special Reports
                          </p>
                          <Link
                            href='/reports'
                            className='text-xs text-orange-1 hover:underline'
                          >
                            View all
                          </Link>
                        </div>
                        <CarouselReport
                          data={specialReports}
                          cardHeight='h-[160px]'
                          spaceBetween={8}
                          showMoreLink='/reports'
                          container={false}
                        />
                      </div>
                    )}
                    {spotlights.length > 0 && (
                      <div>
                        <div className='flex items-center justify-between mb-3'>
                          <p className='text-sm font-semibold'>Spotlights</p>
                          <Link
                            href='/spotlights'
                            className='text-xs text-orange-1 hover:underline'
                          >
                            View all
                          </Link>
                        </div>
                        <CarouselSpotlight
                          data={spotlights}
                          cardHeight='h-[160px]'
                          spaceBetween={8}
                          showMoreLink='/spotlights'
                          container={false}
                        />
                      </div>
                    )}
                    {podcasts.length > 0 && (
                      <div>
                        <div className='flex items-center justify-between mb-3'>
                          <p className='text-sm font-semibold'>Podcasts</p>
                          <Link
                            href='/podcasts-masterclasses'
                            className='text-xs text-orange-1 hover:underline'
                          >
                            View all
                          </Link>
                        </div>
                        <div className='space-y-2'>
                          {podcasts.map((item) => (
                            <Link
                              key={item.id}
                              href={`/podcasts-masterclasses/${item.slug}`}
                              className='block p-3 transition-colors border rounded-lg bg-white/5 border-white/10 hover:bg-white/10'
                            >
                              <div className='flex gap-3'>
                                {(item.thumbnail || item.thumbnail_url) && (
                                  <img
                                    src={item.thumbnail || item.thumbnail_url}
                                    alt={item.title}
                                    className='object-cover w-16 h-16 rounded'
                                  />
                                )}
                                <div className='flex-1 min-w-0'>
                                  <p className='mb-1 text-xs text-orange-1'>
                                    Podcast Episode
                                  </p>
                                  <p className='mb-1 text-xs font-medium line-clamp-2'>
                                    {item.title}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                    {otherContent.length > 0 && (
                      <div>
                        <p className='mb-3 text-sm font-semibold'>
                          Other Content
                        </p>
                        <div className='space-y-2'>
                          {otherContent.map((item) => (
                            <Link
                              key={item.id}
                              href={`/${item.content_type?.slug}/${item.slug}`}
                              className='block p-3 transition-colors border rounded-lg bg-white/5 border-white/10 hover:bg-white/10'
                            >
                              <div className='flex gap-3'>
                                {(item.thumbnail || item.thumbnail_url) && (
                                  <img
                                    src={item.thumbnail || item.thumbnail_url}
                                    alt={item.title}
                                    className='object-cover w-16 h-16 rounded'
                                  />
                                )}
                                <div className='flex-1 min-w-0'>
                                  <p className='mb-1 text-xs text-orange-1'>
                                    {item.content_type?.name}
                                  </p>
                                  <p className='mb-1 text-xs font-medium line-clamp-2'>
                                    {item.title}
                                  </p>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Unicorn List Tab */}
        {activeTab === "list" && (
          <div className='flex flex-col flex-1 min-h-0 m-0 overflow-hidden'>
            {/* Filter Header */}
            <div className='flex-shrink-0 px-4 py-3 border-b border-white/5 space-y-2.5'>
              {/* Row 1: Search + Sector */}
              <div className='flex items-center gap-2'>
                {/* Search */}
                <div className='relative flex-1'>
                  <MagnifyingGlass
                    className='absolute -translate-y-1/2 left-2.5 top-1/2 text-white/40'
                    size={14}
                  />
                  <input
                    type='text'
                    placeholder='Search startup...'
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    className='w-full py-1.5 pl-8 pr-3 text-xs bg-white/5 border border-white/10 rounded-lg outline-none placeholder:text-white/30 focus:border-white/25 transition-colors'
                  />
                </div>

                {/* Sector filter */}
                <div className='relative' ref={sectorDropdownRef}>
                  <button
                    onClick={() => setSectorDropdownOpen((prev) => !prev)}
                    className={`flex items-center gap-1.5 py-1.5 pl-2.5 pr-3 text-xs border rounded-lg transition-colors whitespace-nowrap ${
                      selectedSectorIds.length > 0
                        ? "bg-white/10 border-white/20 text-white"
                        : "bg-white/5 border-white/10 text-white/60 hover:text-white hover:border-white/20"
                    }`}
                  >
                    <Funnel size={14} className='shrink-0' />
                    <span className='truncate max-w-24'>
                      {getSectorLabel()}
                    </span>
                    {selectedSectorIds.length > 0 ? (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          clearSectors();
                        }}
                        className='ml-0.5 hover:text-red-400 transition-colors'
                      >
                        <X size={12} />
                      </span>
                    ) : (
                      <CaretDown size={12} className='shrink-0' />
                    )}
                  </button>

                  {sectorDropdownOpen && (
                    <div className='absolute right-0 z-50 mt-1.5 border rounded-lg w-56 border-white/10 bg-background shadow-xl'>
                      <div className='p-2 border-b border-white/5'>
                        <div className='relative'>
                          <MagnifyingGlass
                            className='absolute -translate-y-1/2 left-2 top-1/2 text-white/40'
                            size={12}
                          />
                          <input
                            type='text'
                            placeholder='Search sectors...'
                            value={sectorSearch}
                            onChange={(e) => setSectorSearch(e.target.value)}
                            className='w-full py-1.5 pl-7 pr-2 text-xs bg-white/5 border border-white/10 rounded-md outline-none placeholder:text-white/30 focus:border-white/25'
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className='overflow-y-auto max-h-56'>
                        <div
                          onClick={clearSectors}
                          className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/5'
                        >
                          <span
                            className={
                              selectedSectorIds.length === 0
                                ? "text-orange-400"
                                : ""
                            }
                          >
                            All Sectors
                          </span>
                          {selectedSectorIds.length === 0 && (
                            <Check
                              weight='bold'
                              size={14}
                              className='text-orange-400'
                            />
                          )}
                        </div>
                        {filteredSectorList.map((sector) => {
                          const isSelected = selectedSectorIds.includes(
                            sector.id.toString(),
                          );
                          return (
                            <div
                              key={sector.id}
                              onClick={() => toggleSector(sector.id)}
                              className='flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-white/5'
                            >
                              <span
                                className={isSelected ? "text-orange-400" : ""}
                              >
                                {sector.name}
                              </span>
                              {isSelected && (
                                <Check
                                  weight='bold'
                                  size={14}
                                  className='text-orange-400'
                                />
                              )}
                            </div>
                          );
                        })}
                        {filteredSectorList.length === 0 && (
                          <div className='px-3 py-4 text-xs text-center text-white/30'>
                            No sectors found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Status chips */}
              <div className='flex items-center gap-1.5 flex-wrap'>
                {[
                  { key: "current", label: "Current" },
                  { key: "fallen", label: "Fallen" },
                  { key: "alumni", label: "Alumni" },
                ].map(({ key, label }) => {
                  const count = statusCounts?.[key] || 0;
                  const isDisabled = count === 0;
                  const isActive = statusFilter?.[key] && !isDisabled;
                  return (
                    <button
                      key={key}
                      onClick={() => toggleStatus(key)}
                      disabled={isDisabled}
                      className={`px-2.5 py-0.5 text-[11px] font-medium rounded-full border transition-colors ${
                        isDisabled
                          ? "border-white/5 text-white/20 cursor-not-allowed"
                          : isActive
                            ? "bg-white border-white text-black cursor-pointer"
                            : "border-white/15 text-white hover:bg-white/5 cursor-pointer"
                      }`}
                    >
                      {label}
                      <span
                        className={`ml-1 text-[9px] rounded-full size-5 -mr-1.5 inline-flex items-center justify-center ${
                          isDisabled
                            ? "bg-white/5 text-white/20"
                            : isActive
                              ? "bg-background text-white"
                              : "bg-white/10 text-white"
                        }`}
                      >
                        {count.toLocaleString()}
                      </span>
                    </button>
                  );
                })}
                <CustomTooltip
                  trigger={
                    <span className='text-white/40 cursor-help'>
                      <Info className='size-3.5' />
                    </span>
                  }
                  content='Fallen Unicorn: Previously reached US $1B+, but latest known private valuation is now below US $1B. Alumni: Company has gone public or has been acquired.'
                />
              </div>
            </div>

            {/* Unicorn List */}
            <div ref={listScrollRef} className='flex-1 min-h-0 overflow-y-auto'>
              <div className='px-4 pb-6'>
                {loading ? (
                  <div className='mt-3 space-y-3'>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div
                        key={index}
                        className='p-3 border rounded-lg bg-white/5 border-white/10 animate-pulse'
                      >
                        <div className='flex gap-3'>
                          <div className='rounded-full size-12 bg-white/10 shrink-0' />
                          <div className='flex-1 space-y-2'>
                            <div className='w-32 h-3 rounded bg-white/10' />
                            <div className='w-24 h-2 rounded bg-white/10' />
                            <div className='w-20 h-2 rounded bg-white/10' />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : unicorns.length > 0 ? (
                  <div className='mt-3 space-y-3'>
                    {unicorns.map((startup, index) => {
                      const isLastElement = unicorns.length === index + 1;
                      const status = getUnicornStatus(startup);
                      const statusColorClass =
                        status === "Decacorn"
                          ? "bg-purple-500/20 text-purple-300"
                          : status === "Alumni"
                            ? "bg-blue-500/20 text-blue-300"
                            : status === "Fallen"
                              ? "bg-red-500/20 text-red-300"
                              : "bg-yellow-500/20 text-yellow-300";

                      const originType =
                        startup.display_origin_type ||
                        (() => {
                          const foundedId =
                            startup.founded_country_id ||
                            startup.founded_country?.id;
                          const hqId =
                            startup.country_id || startup.country?.id;
                          if (!foundedId) return null;
                          if (foundedId === hqId) return "homegrown";
                          return "transplant";
                        })();

                      return (
                        <div
                          key={startup.id || index}
                          ref={isLastElement ? lastUnicornElementRef : null}
                          className='p-3 border rounded-xl bg-white/5 border-white/5'
                        >
                          <Link
                            href={`/startups/${startup.slug}-${startup.id}`}
                            target='_blank'
                            className='block'
                          >
                            {/* Header: logo + name + status */}
                            <div className='flex gap-3'>
                              <div className='relative overflow-hidden rounded-full size-10 bg-white/5 shrink-0 ring-[2px] ring-white/15'>
                                {startup.logo_url ? (
                                  <Image
                                    layout='fill'
                                    src={startup.logo_url}
                                    alt={startup.name || "Startup logo"}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className='flex items-center justify-center w-full h-full text-sm font-bold bg-gradient-to-br from-yellow-400 to-red-500'>
                                    {startup.name
                                      ? startup.name.charAt(0).toUpperCase()
                                      : "S"}
                                  </div>
                                )}
                              </div>
                              <div className='flex-1 min-w-0'>
                                <div className='flex items-center justify-between gap-2'>
                                  <p className='text-base font-semibold leading-tight truncate'>
                                    {startup.name || "Unknown Startup"}
                                  </p>
                                  <div className='flex items-center gap-1.5 shrink-0'>
                                    <span
                                      className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${statusColorClass}`}
                                    >
                                      {status}
                                    </span>
                                    <ArrowUpRight
                                      weight='bold'
                                      className='text-base opacity-40'
                                    />
                                  </div>
                                </div>

                                {/* HQ location */}
                                <p className='text-xs opacity-40 mt-0.5'>
                                  {startup.city?.name
                                    ? `${startup.city.name}, `
                                    : ""}
                                  {startup.country?.country_name || "Unknown"}
                                </p>

                                {/* Established In + Origin */}
                                {(startup.founded_country || originType) && (
                                  <div className='flex items-center gap-2 mt-1.5 flex-wrap'>
                                    {startup.founded_country && (
                                      <div className='flex items-center gap-1'>
                                        <span
                                          className={`inline-block size-3.5 rounded-full bg-cover fib ${getOriginFlag(startup.founded_country.country_name)} fis ring-1 ring-white/20`}
                                        />
                                        <span className='text-[11px] text-white/50'>
                                          {startup.founded_country.prefix ||
                                            startup.founded_country
                                              .country_name}
                                        </span>
                                      </div>
                                    )}
                                    {originType && (
                                      <span
                                        className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full border ${ORIGIN_TYPE_STYLES[originType] || ""}`}
                                      >
                                        {ORIGIN_TYPE_LABELS[originType] ||
                                          originType}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Funding + Valuation */}
                            <div className='grid grid-cols-2 py-3 mt-3 text-center divide-x rounded-lg bg-white/5 divide-white/5'>
                              <div className='flex flex-col px-3'>
                                {formatCurrency(startup.total_round_amount) ===
                                "Undisclosed" ? (
                                  <p className='flex items-center justify-center flex-1 text-base font-medium opacity-40'>
                                    Undisclosed
                                  </p>
                                ) : (
                                  <p className='flex-1 text-xl font-semibold tracking-tight text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                                    {formatCurrency(startup.total_round_amount)}
                                  </p>
                                )}
                                <p className='mt-1 text-[11px] opacity-40'>
                                  Total Fundraised
                                </p>
                              </div>
                              <div className='flex flex-col px-3'>
                                {formatCurrency(
                                  startup.last_known_post_money_valuation,
                                ) === "Undisclosed" ? (
                                  <p className='flex items-center justify-center flex-1 text-base font-medium opacity-40'>
                                    Undisclosed
                                  </p>
                                ) : (
                                  <p className='flex-1 text-xl font-semibold tracking-tight text-transparent bg-clip-text bg-gradient-to-tr from-yellow-1 to-red-1'>
                                    {formatCurrency(
                                      startup.last_known_post_money_valuation,
                                    )}
                                  </p>
                                )}
                                <p className='mt-1 text-[11px] opacity-40'>
                                  Latest Valuation
                                </p>
                              </div>
                            </div>

                            {/* Stats rows — mirrors all desktop table columns */}
                            <div className='mt-2 divide-y divide-dashed divide-white/5'>
                              <div className='flex items-center justify-between w-full py-1.5'>
                                <p className='text-xs opacity-40'>
                                  Valuation as of
                                </p>
                                <p className='text-xs'>
                                  {formatDate(
                                    startup.last_known_post_money_valuation_date,
                                  )}
                                </p>
                              </div>
                              <div className='flex items-center justify-between w-full py-1.5'>
                                <p className='text-xs opacity-40'>
                                  Became Unicorn
                                </p>
                                <p className='text-xs'>
                                  {formatDate(startup.unicorn_on)}
                                </p>
                              </div>
                              <div className='flex items-center justify-between w-full py-1.5'>
                                <p className='text-xs opacity-40'>
                                  Time to Unicorn
                                </p>
                                <p className='text-xs'>
                                  {formatTimeToUnicornYears(startup)}
                                </p>
                              </div>
                              <div className='flex items-center justify-between w-full py-1.5'>
                                <p className='text-xs opacity-40'>
                                  Last Reported Stage
                                </p>
                                <p
                                  className={`text-xs ${
                                    getLatestStage(startup) === "IPO"
                                      ? "text-green-300"
                                      : getLatestStage(startup) === "Acquired"
                                        ? "text-blue-300"
                                        : "text-gray-300"
                                  }`}
                                >
                                  {getLatestStage(startup)}
                                </p>
                              </div>
                            </div>
                          </Link>
                        </div>
                      );
                    })}

                    {loadingMore && (
                      <div className='flex items-center justify-center gap-2 py-4'>
                        <div className='w-4 h-4 border-2 rounded-full border-white/20 border-t-white/60 animate-spin' />
                        <span className='text-sm text-white/60'>
                          Loading more...
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className='py-8 text-sm text-center text-white/50'>
                    {localSearch
                      ? `No unicorns found matching "${localSearch}"`
                      : "No unicorns found"}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Tabs>
    </div>
  );
}
